import { createContext, useCallback, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios, { clearApiCache, LONG_CACHE_TTL } from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';
import { normalizePlanEntitlements } from '../utils/planEntitlements';
import { fetchWorkspaces, resolveWorkspace } from '../utils/workspaces';
import { identifyPostHogUser, resetPostHog, capturePostHogEvent } from '../lib/posthog';

// ----------------------------------------------------------------------

const ACTIVE_WORKSPACE_KEY = 'activeWorkspaceId';

async function loadWorkspaceState(currentWorkspace) {
  let workspaces;
  try {
    workspaces = await fetchWorkspaces();
  } catch (error) {
    workspaces = [];
  }
  if (!workspaces.length && currentWorkspace) workspaces = [currentWorkspace];
  const workspace = resolveWorkspace(
    workspaces,
    currentWorkspace,
    window.localStorage.getItem(ACTIVE_WORKSPACE_KEY),
    new URLSearchParams(window.location.search).get('workspaceId'),
  );
  if (workspace?.id) window.localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspace.id);
  return { workspace, workspaces };
}

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  credits: null,
  workspace: null,
  workspaces: [],
  onboarding: null,
  planEntitlements: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, credits, workspace, workspaces, onboarding, planEntitlements } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      credits,
      workspace,
      workspaces,
      onboarding,
      planEntitlements,
    };
  },
  UPDATE: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user, credits, workspace, workspaces, onboarding, planEntitlements } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      credits,
      workspace,
      workspaces,
      onboarding,
      planEntitlements,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    credits: null,
    workspace: null,
    workspaces: [],
    onboarding: null,
    planEntitlements: null,
  }),
  REGISTER: (state, action) => {
    const { user, onboarding } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      onboarding,
    };
  },
  SET_ONBOARDING: (state, action) => ({
    ...state,
    onboarding: action.payload.onboarding,
  }),
  SET_WORKSPACE: (state, action) => ({
    ...state,
    workspace: action.payload.workspace,
  }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  loginWithSession: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  setOnboarding: () => Promise.resolve(),
  selectWorkspace: () => {},
  refreshAccount: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (state.isAuthenticated) identifyPostHogUser(state.user?.id || state.onboarding?.userId);
  }, [state.isAuthenticated, state.user?.id, state.onboarding?.userId]);

  useEffect(() => {
    const initialize = async () => {
      try {
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const oauthAccessToken = hashParams.get('access_token');
        const oauthRefreshToken = hashParams.get('refresh_token');
        const oauthType = hashParams.get('type');

        if (oauthAccessToken && oauthRefreshToken && oauthType === 'oauth') {
          setSession(oauthAccessToken, oauthRefreshToken);
          window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);

          const response = await axios.get('/api/account/my-account', { cacheTtl: LONG_CACHE_TTL });
          const { credits, workspace, onboarding, planEntitlements, ...rest } = response.data;
          const workspaceState = await loadWorkspaceState(workspace);

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: rest,
              credits,
              ...workspaceState,
              onboarding,
              planEntitlements: normalizePlanEntitlements(planEntitlements || { plan: onboarding?.selectedPlan }),
            },
          });
          return;
        }

        const accessToken = window.localStorage.getItem('accessToken');
        const refreshToken = window.localStorage.getItem('refreshToken');

        if (accessToken && refreshToken && isValidToken(accessToken)) {
          setSession(accessToken, refreshToken);

          const response = await axios.get('/api/account/my-account', { cacheTtl: LONG_CACHE_TTL });
          const {credits, workspace, onboarding, planEntitlements, ...rest} = response.data;
          const workspaceState = await loadWorkspaceState(workspace);

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: rest,
              credits,
              ...workspaceState,
              onboarding,
              planEntitlements: normalizePlanEntitlements(planEntitlements || { plan: onboarding?.selectedPlan }),
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
              credits: null,
              workspace: null,
              workspaces: [],
              onboarding: null,
              planEntitlements: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            credits: null,
            workspace: null,
            workspaces: [],
            onboarding: null,
            planEntitlements: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (username, password) => {
    const response = await axios.post('/api/auth/login', {
      username,
      password,
    });
    const { accessToken, refreshToken, profile, credits, workspace, onboarding, planEntitlements } = response.data;
    setSession(accessToken, refreshToken);
    const workspaceState = await loadWorkspaceState(workspace);
    dispatch({
      type: 'LOGIN',
      payload: {
        user: profile,
        credits,
        ...workspaceState,
        onboarding,
        planEntitlements: normalizePlanEntitlements(planEntitlements || { plan: onboarding?.selectedPlan }),
      },
    });
  };

  const loginWithSession = async (accessToken, refreshToken) => {
    setSession(accessToken, refreshToken);
    const response = await axios.get('/api/account/my-account', { cacheTtl: LONG_CACHE_TTL });
    const { credits, workspace, onboarding, planEntitlements, ...rest } = response.data;
    const workspaceState = await loadWorkspaceState(workspace);
    dispatch({
      type: 'LOGIN',
      payload: {
        user: rest,
        credits,
        ...workspaceState,
        onboarding,
        planEntitlements: normalizePlanEntitlements(planEntitlements || { plan: onboarding?.selectedPlan }),
      },
    });
  };

  const register = async (username, password, name, referralCode) => {
    const response = await axios.post('/api/account/signup', {
      username,
      password,
      name,
      ...(referralCode ? {referralCode} : {}),
    });
    const { accessToken, refreshToken, user, onboarding } = response.data;

    setSession(accessToken, refreshToken);
    dispatch({
      type: 'REGISTER',
      payload: {
        user,
        onboarding,
      },
    });
    identifyPostHogUser(user?.id || onboarding?.userId);
    capturePostHogEvent('user_registered');
  };

  const setOnboarding = (onboarding) => {
    dispatch({
      type: 'SET_ONBOARDING',
      payload: { onboarding },
    });
  };

  const selectWorkspace = useCallback((workspace) => {
    if (!workspace?.id) return;
    clearApiCache();
    window.localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspace.id);
    dispatch({ type: 'SET_WORKSPACE', payload: { workspace } });
  }, []);

  const refreshAccount = async () => {
    const response = await axios.get('/api/account/my-account', { cache: false });
    const { credits, workspace: accountWorkspace, onboarding, planEntitlements, ...rest } = response.data;
    const workspaceState = await loadWorkspaceState(state.workspace || accountWorkspace);
    dispatch({
      type: 'INITIALIZE',
      payload: {
        isAuthenticated: true,
        user: rest,
        credits,
        ...workspaceState,
        onboarding,
        planEntitlements: normalizePlanEntitlements(planEntitlements || { plan: onboarding?.selectedPlan }),
      },
    });
  };

  const logout = async () => {
    resetPostHog();
    setSession(null, null);
    dispatch({ type: 'LOGOUT' });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        loginWithSession,
        logout,
        register,
        setOnboarding,
        selectWorkspace,
        refreshAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
